import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import CostCenterGroupsSettings from "../../dp/components/CostCenterGroupsSettings";
import {
  createCostCenterApi,
  deleteCostCenterApi,
  DpCostCenter,
  DpCostCenterGroup,
  listCostCenterGroupsApi,
  listCostCentersApi,
  updateCostCenterApi,
} from "../../dp/api/settingsApi";
import { listStoresApi } from "../api/financeiroApi";
import type { FinStore } from "../api/finTypes";

type Props = { token: string; canWrite: boolean };

const BASE = "/financeiro/settings" as const;
const emptyForm = { code: "", name: "", storeId: "", isActive: true, groupId: "" };

/**
 * Centros de custo no Financeiro (04/11/2026): o mesmo cadastro da aba do DP
 * (centros, lojas e grupos da DRE), pelas rotas `/financeiro/settings/*`.
 */
export default function CostCentersSettings({ token, canWrite }: Props) {
  const [costCenters, setCostCenters] = useState<DpCostCenter[]>([]);
  const [groups, setGroups] = useState<DpCostCenterGroup[]>([]);
  const [stores, setStores] = useState<FinStore[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{ id?: string } | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<DpCostCenter | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, g] = await Promise.all([listCostCentersApi(token, true, BASE), listCostCenterGroupsApi(token, true, BASE)]);
      setCostCenters(c);
      setGroups(g);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os centros de custo.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    load();
    listStoresApi(token)
      .then(setStores)
      .catch(() => setStores([]));
  }, [token, load]);

  const open = (c?: DpCostCenter) => {
    setForm(
      c
        ? { code: c.code, name: c.name, storeId: c.storeId ?? "", isActive: c.isActive, groupId: c.groupId ?? "" }
        : emptyForm
    );
    setModal({ id: c?.id });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modal) return;
    if (!form.name.trim() || !form.groupId) {
      toast.error("Informe o nome e o grupo.");
      return;
    }
    const payload = {
      name: form.name.trim(),
      storeId: form.storeId || null,
      isActive: form.isActive,
      groupId: form.groupId,
    };
    setSaving(true);
    try {
      if (modal.id) await updateCostCenterApi(token, modal.id, payload, BASE);
      else await createCostCenterApi(token, payload, BASE);
      toast.success("Centro de custo salvo.");
      setModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar centro de custo.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!removing) return;
    setSaving(true);
    try {
      await deleteCostCenterApi(token, removing.id, BASE);
      toast.success("Centro de custo excluído.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir centro de custo.");
    } finally {
      setRemoving(null);
      setSaving(false);
    }
  };

  if (loading) return <p className="dpEmpty">Carregando…</p>;

  return (
    <>
      {canWrite ? (
        <div className="dpControls">
          <button className="dpBtn dpBtn--primary" onClick={() => open()}>
            Novo centro de custo
          </button>
        </div>
      ) : null}

      <CostCenterGroupsSettings token={token} groups={groups} onChanged={load} apiBase={BASE} canWrite={canWrite} />

      <p className="dpNotice">
        Cada centro de custo tem uma loja só, ou nenhuma (há centro de custo que não corresponde a loja nenhuma, como o
        administrativo). Este cadastro é o mesmo da aba do DP.
      </p>

      <div className="dpTableWrapper">
        <table className="dpTable">
          <thead>
            <tr>
              <th>Código</th>
              <th>Nome</th>
              <th>Grupo</th>
              <th>Loja</th>
              <th>Situação</th>
              {canWrite ? <th style={{ width: 160 }}>Ações</th> : null}
            </tr>
          </thead>
          <tbody>
            {costCenters.length === 0 ? (
              <tr>
                <td colSpan={canWrite ? 6 : 5} className="dpEmpty">
                  Nenhum centro de custo cadastrado.
                </td>
              </tr>
            ) : (
              costCenters.map((c) => (
                <tr key={c.id}>
                  <td>{c.code}</td>
                  <td>{c.name}</td>
                  <td>
                    {c.group ? `${c.group.code} — ${c.group.name}` : "—"}
                  </td>
                  <td>{c.store?.nomeFantasia ?? "—"}</td>
                  <td>
                    <span className={`dpBadge ${c.isActive ? "dpBadge--green" : "dpBadge--gray"}`}>
                      {c.isActive ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  {canWrite ? (
                    <td>
                      <div className="dpTable__actions">
                        <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => open(c)}>
                          Editar
                        </button>
                        <button className="dpBtn dpBtn--danger dpBtn--small" onClick={() => setRemoving(c)}>
                          Excluir
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modal ? (
        <div className="dpModalOverlay">
          <div className="dpModal" role="dialog" aria-modal="true" aria-label="Centro de custo">
            <header className="dpModal__header">
              <h2 className="dpModal__title">{modal.id ? "Editar centro de custo" : "Novo centro de custo"}</h2>
              <button className="dpModal__close" onClick={() => setModal(null)} disabled={saving} aria-label="Fechar">
                &times;
              </button>
            </header>

            <form onSubmit={save}>
              <div className="dpModal__body">
                <p className="dpFormHint">
                  {modal.id ? (
                    <>
                      Código <strong>{form.code}</strong> (gerado pelo sistema; muda se o centro trocar de grupo).
                    </>
                  ) : (
                    "O código é gerado ao salvar: o próximo número dentro do grupo (ex.: grupo 2 → 2.1, 2.2…)."
                  )}
                </p>

                <div className="dpFormGroup">
                  <label htmlFor="finCcName">Nome *</label>
                  <input
                    id="finCcName"
                    className="dpInput"
                    value={form.name}
                    maxLength={120}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={saving}
                  />
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="finCcGroup">Grupo *</label>
                  <select
                    id="finCcGroup"
                    className="dpSelect"
                    value={form.groupId}
                    onChange={(e) => setForm((f) => ({ ...f, groupId: e.target.value }))}
                    disabled={saving}
                  >
                    <option value="">Escolha</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id} disabled={!g.isActive && g.id !== form.groupId}>
                        {g.code} — {g.name}
                        {g.isActive ? "" : " (inativo)"}
                      </option>
                    ))}
                  </select>
                  <span className="dpFormHint">Onde o centro aparece na DRE.</span>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="finCcStore">Loja</label>
                  <select
                    id="finCcStore"
                    className="dpSelect"
                    value={form.storeId}
                    onChange={(e) => setForm((f) => ({ ...f, storeId: e.target.value }))}
                    disabled={saving}
                  >
                    <option value="">Sem loja</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <span className="dpFormHint">O centro de custo é atrelado a uma loja só.</span>
                </div>

                <div className="dpFormGroup">
                  <label>
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                      disabled={saving}
                    />{" "}
                    Ativo
                  </label>
                </div>
              </div>

              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setModal(null)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  {saving ? "Salvando..." : "Salvar"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      <EcoConfirmDialog
        open={removing !== null}
        title="Excluir centro de custo?"
        message={
          removing
            ? `"${removing.name}" será excluído. Centro de custo já usado em contrato não se exclui: nesse caso, inative.`
            : ""
        }
        confirmLabel="Excluir"
        tone="danger"
        busy={saving}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}
